import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { EMPTY, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, share, switchMap } from 'rxjs/operators';

import { ApplicationConfigurationSetting, InstalledApplication } from '@rocksoft/mdm-sdk-v2';

import { AsyncPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { AppleDeviceProfileResolverActions, selectProfileItemByType } from '@era/stores';
import { Store } from '@ngrx/store';
import { ErrorModel, isNotNullOrUndefined } from '@rocksoft/shared';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../utilities/command-spinner/command-spinner.component';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';
import { AppleInstalledApplicationComponent } from '../apple-installed-application/apple-installed-application.component';
import { ManagedApplicationStoreActions, ManagedApplicationStoreSelectors } from '../stores';
import { ApplicationConfigurationComponent } from './application-configuration.component';

@Component({
  selector: 'ml-mdm-apple-app',
  templateUrl: './apple-app.component.html',
  styleUrls: ['./apple-app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AppleInstalledApplicationComponent, CommandSpinnerComponent, ApplicationConfigurationComponent, AsyncPipe],
})
export class AppleAppComponent implements OnDestroy {
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly store = inject(Store);

  private subscriptions: Subscription[] = [];

  private readonly applicationConfigurationComponent = viewChild.required(ApplicationConfigurationComponent);

  public readonly app = input<InstalledApplication>();

  applicationConfiguration: Observable<ApplicationConfigurationSetting>;

  private readonly _udidSignal = computed(() => this.app()?.udid);
  private readonly _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  public readonly commandProgress$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) => {
      if (!_udid) {
        return EMPTY;
      }
      return merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'Settings'));
    })
  );

  private _commandProgressManagedApplicationConfiguration: Subject<CommandProgress> = new Subject<CommandProgress>();
  commandProgressManagedApplicationConfiguration: Observable<CommandProgress>;

  public error: ErrorModel;
  public success = false;

  public readonly isManaged$: Observable<boolean>;

  constructor() {
    const app$ = toObservable(this.app).pipe(filter(isNotNullOrUndefined));

    this.isManaged$ = app$.pipe(
      switchMap((app) =>
        this.store.select(
          ManagedApplicationStoreSelectors.selectManagedApplicationsByUdidAndIdentifier(app.udid, app.identifier ?? '') // Identifier can't be required: "This key will always be present on iOS/tvOS but may be missing on macOS."
        )
      ),
      map((managedApplication) => !!managedApplication)
    );

    effect(() => {
      const app = this.app();
      if (!app) return;

      this.store.dispatch(
        ManagedApplicationStoreActions.ManagedApplicationsContainerActions.managedApplicationRequested({
          udid: app.udid,
        })
      );

      this.subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
      this.subscriptions = [];

      this.applicationConfiguration = this.store
        .select(selectProfileItemByType('application-configuration', app.identifier ?? '')) // Identifier can't be required: "This key will always be present on iOS/tvOS but may be missing on macOS."
        .pipe(
          map((config) => {
            if (!config) {
              return { identifier: app.identifier } as ApplicationConfigurationSetting;
            }
            return { identifier: app.identifier, ...(config as object) } as ApplicationConfigurationSetting;
          }),
          catchError((error) => this.handleError(error))
        );

      this.track(
        this.commandProgress$
          .pipe(
            filter((progress) => progress.status === 'Acknowledged'),
            distinctUntilChanged()
          )
          .subscribe(() => {
            this.queryManagedApplicationConfiguration();
          })
      );

      // TODO: Move to central service
      this.commandProgressManagedApplicationConfiguration = merge(
        this._commandProgressManagedApplicationConfiguration,
        this.commandProgressService.commandsOfRequestType(app.udid, 'ManagedApplicationConfiguration')
      ).pipe(share());

      this.track(
        this.commandProgressManagedApplicationConfiguration
          .pipe(
            filter((progress) => progress.status === 'Acknowledged'),
            distinctUntilChanged()
          )
          .subscribe((progress) => {
            this.store.dispatch(
              AppleDeviceProfileResolverActions.profileRequested({
                udid: progress.udid,
                profileType: 'application-configuration',
              })
            );
          })
      );
    });
  }

  public saveApplicationConfiguration(applicationConfiguration: ApplicationConfigurationSetting) {
    // Try to stop editing on both components
    if (!this.applicationConfigurationComponent().doneEditing()) {
      return;
    }

    const app = this.app();
    if (!app) {
      return;
    }

    const udid = app.udid;

    this.error = {};
    this.success = false;

    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'Settings',
    };
    this._commandProgress.next(command);

    this.mdmService.saveApplicationConfiguration(udid, applicationConfiguration).subscribe({
      next: () => {
        this.success = true;
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  handleError(error: ErrorModel): Observable<ApplicationConfigurationSetting> {
    const app = this.app();
    if (!app) {
      return EMPTY;
    }

    if ((error as unknown as { error?: { code?: string } })?.error?.code === 'NotFound') {
      // TODO Log as error
      return of({ identifier: app.identifier ?? '' }); // Identifier can't be required: "This key will always be present on iOS/tvOS but may be missing on macOS."
    }

    this.error = error;
    return EMPTY;
  }

  private queryManagedApplicationConfiguration() {
    const app = this.app();
    if (!app) {
      return;
    }

    const command: CommandProgress = {
      udid: app.udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'ManagedApplicationConfiguration',
    };
    this._commandProgressManagedApplicationConfiguration.next(command);
    this.track(this.mdmService.getManagedApplicationConfigurations(app.udid).subscribe());
  }

  private track(subscription: Subscription) {
    this.subscriptions.push(subscription);
  }
}
